import { Form as InertiaForm, Link } from "@inertiajs/react"
import { ArrowLeft, Save } from "lucide-react"
import { Button } from "@/components/ui/button.tsx"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card.tsx"
import { Input } from "@/components/ui/input.tsx"
import { useMemo, useState } from "react"
import type { UserType } from "./types.ts"

interface CommunityOption {
    id: string
    code: string
    name: string
}

interface CommunitiesPageProps {
    user: UserType
    communities: CommunityOption[]
    assigned_community_ids: string[]
}

export default function Communautés({ user, communities, assigned_community_ids }: CommunitiesPageProps) {
    const [search, setSearch] = useState("")

    const filteredCommunities = useMemo(() => {
        const term = search.trim().toLowerCase()
        if (term.length === 0) {
            return communities
        }

        return communities.filter((community) =>
            community.name.toLowerCase().includes(term) || community.code.toLowerCase().includes(term)
        )
    }, [communities, search])

    return (
        <div className="space-y-6 p-8">
            <div className="flex items-center gap-4">
                <Link href="/admin/users">
                    <Button variant="ghost" size="sm">
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Retour
                    </Button>
                </Link>
                <div>
                    <h1 className="text-3xl font-bold text-foreground">Assigner des communautés</h1>
                    <p className="text-muted-foreground">
                        {user.first_name} {user.last_name} ({user.email})
                    </p>
                </div>
            </div>

            <InertiaForm method="patch" action={`/admin/users/${user.id}/update_communities`}>
                {({ processing }) => (
                    <Card>
                        <CardHeader>
                            <CardTitle>Communautés</CardTitle>
                            <CardDescription>Sélectionnez les communautés associées à cet utilisateur.</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <Input
                                type="text"
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="Filtrer par nom ou code"
                            />

                            <div className="max-h-[460px] space-y-3 overflow-y-auto rounded-md border p-4">
                                {filteredCommunities.length === 0 ? (
                                    <p className="text-sm text-muted-foreground">Aucune communauté trouvée.</p>
                                ) : (
                                    filteredCommunities.map((community) => (
                                        <label key={community.id} className="flex items-center gap-3">
                                            <input
                                                type="checkbox"
                                                name="community_ids[]"
                                                value={community.id}
                                                defaultChecked={assigned_community_ids.includes(community.id)}
                                            />
                                            <div>
                                                <p className="text-sm font-medium">{community.name}</p>
                                                <p className="text-xs text-muted-foreground">{community.code}</p>
                                            </div>
                                        </label>
                                    ))
                                )}
                            </div>

                            <div className="flex justify-end gap-2">
                                <Link href="/admin/users">
                                    <Button type="button" variant="outline">
                                        Annuler
                                    </Button>
                                </Link>
                                <Button type="submit" disabled={processing}>
                                    <Save className="mr-2 h-4 w-4" />
                                    Enregistrer
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </InertiaForm>
        </div>
    )
}
